import * as React from "react";

import "./select-user-panel.scss";

import {cs}               from "@Common/react/chain-services";
import {consumeContext}   from "@Common/react/context";
import {Load}             from "@Common/react/load";
import {UseState}         from "@Common/react/use-state";
import {Types}            from "@Common/types";
import {SearchableSelect} from "@Common/ui-components/searchable-select/searchable-select";
import {sortMulti}        from "@Common/utils/collections";
import {chain}            from "@Common/utils/fs";
import {omit}             from "@Common/utils/objects";

import {makeBranchAsSublabelMap} from "./make-branch-as-sublabel-map";
import {mapAgentAsLabel}         from "./map-agent-as-label";
import {mapAgentAsSublabel}      from "./map-agent-as-sublabel";
import {mapBranchAsLabel}        from "./map-branch-as-label";
import {mapSearchAsBranchFilter} from "./map-search-as-branch-filter";

export const SelectUserPanel = ({multiBranch=true, forcedBranchId=undefined, limitByBenefit = '', next}) => cs(

    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("userLevels"),
    ["state", ({session}, next) => UseState({
        defaultValue: session.value,
        next,
    })],

    ["branchList", ({apis}, next) => Load({
        fetch: () => !multiBranch || forcedBranchId ? null : apis.next.getBranchList({
            activeOfficesOnly:true
        }),
        next,
    })],
    ["agentList", ({state, apis}, next) => {
        const branchId = forcedBranchId ?? state.value?.branchId;
        return Load({
            _key: branchId,
            fetch: () => !branchId ? [] : apis.next.getAgentList(branchId, 1, limitByBenefit),
            next,
        });
    }],
    ["isInternalUser", ({userLevels}, next) => next(userLevels?.internalUser)],
    ({branchList, state, session, isInternalUser, agentList}) => next({
        value: state.value,
        clearSelection: () => state.onChange({}),
        render: () => (
            <div className="select-user-panel-9ks">
                {multiBranch && (
                    <div className="form-group">
                        <div className="control-label">
                            Office
                        </div>

                        {SearchableSelect<Branch>({
                            getSublabel: makeBranchAsSublabelMap({forInternalUser: isInternalUser}),
                            list: chain(
                                branchList ?? (session.data.branchName ? [{idBranch: session.value.branchId, branchName: session.data.branchName}] : []),
                                (_) => sortMulti(_, [
                                    (branch) => branch["branchName"]?.toLowerCase(),
                                ]),
                            ),
                            loading: !branchList,
                            placeholder: "Please select an office",
                            getLabel: mapBranchAsLabel,
                            mapSearchAsItemFilter: mapSearchAsBranchFilter,
                            onSelect: async (branch) => {
                                state.onChange(!branch ? {} : {
                                    branchId: branch.idBranch,
                                });
                            },
                            isSelected: (branch) => state.value?.branchId === branch.idBranch,

                            className: "office",
                            searchLabel: "Search an Office",
                            xdQaName: "Office",
                            scrollCloses: false,
                        })}
                    </div>
                )}
                <div className="form-group">
                    <div className="control-label">
                        User
                    </div>

                    {SearchableSelect({
                        list: [
                            ...( !limitByBenefit ? ["branch_order"] : [] ),
                            ...chain(
                                agentList ?? (session.data.agentNameFirst ? [{
                                    idAgent: session.value.agentId,
                                    agentNameFirst: session.data.agentNameFirst,
                                    agentNameLast: session.data.agentNameLast,
                                }] : []),
                                (_) => sortMulti(_, [
                                    (agent) => agent["agentNameLast"]?.toLowerCase(),
                                    (agent) => agent["agentNameFirst"]?.toLowerCase(),
                                ])
                            ),
                        ],
                        loading: !agentList,
                        disabled: multiBranch && !state.value?.branchId,
                        getLabel: (agent) => agent === "branch_order" ? (
                            "Office Order"
                        ) : mapAgentAsLabel(agent),
                        getSublabel: isInternalUser ? (agent) => agent.idAgent && mapAgentAsSublabel(agent) : null,
                        placeholder: "Please select a user",
                        onSelect: async (agent) => {
                            if (!agent) {
                                state.change((s) => omit(s, ["agentId", "isBranchOrder"]));
                            } else if (agent === "branch_order") {
                                state.change((s) => ({
                                    ...omit(s, ["agentId"]),
                                    isBranchOrder: true,
                                }))
                            } else {
                                state.change((s) => ({
                                    ...omit(s, ["isBranchOrder"]),
                                    agentId: agent.idAgent,
                                }))
                            }
                        },
                        isSelected: (agent) => agent === "branch_order" ? (
                            state.value?.isBranchOrder
                        ) : (
                            state.value?.agentId === agent.idAgent
                        ),

                        searchLabel: "Search a User",
                        xdQaName: "User",
                        scrollCloses: false,
                    })}
                </div>
            </div>
        ),
    }),
);


type Branch = Types.Apis.Payloads.Next.Branch.GetAll.Response[number];

